import type { ComputedRef, Ref } from 'vue';
import type { LocationQuery } from 'vue-router';
import type {
  InitialData as EtaReportSearchFormInitialData,
  ValidData as EtaReportSearchFormValidData,
} from '@/components/searchForms/EtaReportSearchForm.vue';

import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { format } from 'date-fns-tz';
import _isEqual from 'lodash/isEqual';

import { useUserStore } from '@/stores/useUserStore';
import {
  fetchGetETAReport,
  fetchGetReportEtaFile,
  fetchSendETAReport,
} from '@/helpers/fetchers';
import * as routeNames from '@/constants/routeNames';
import { ETAReportResponseData } from '@/models/reports';

export interface EtaReportViewComposable {
  search: (formData: EtaReportSearchFormValidData) => void;
  initialData: ComputedRef<EtaReportSearchFormInitialData>;
  isFetchingData: Ref<boolean>;
  isSendingReport: Ref<boolean>;
  isDownloadingPDF: Ref<boolean>;
  isDownloadingCSV: Ref<boolean>;
  etaReportData: Ref<ETAReportResponseData>;
  handleSendClick: () => void;
  downloadPdfETAReport: () => void;
  downloadCsvETAReport: () => void;
}

export default function useEtaReportView(
  queryCurPage: Readonly<Ref<number>>,
  queryPageSize: Readonly<Ref<number>>,
  queryCustomer: Readonly<Ref<string | undefined>>,
  queryDate: Readonly<Ref<Date | undefined>>,
): EtaReportViewComposable {
  const router = useRouter();
  const route = useRoute();
  const userStore = useUserStore();

  const userTimeZone = computed(() => userStore.user.timezone);

  const initialData = computed<EtaReportSearchFormInitialData>(() => {
    let date;
    if (queryDate.value) {
      date = format(queryDate.value, 'yyyy-MM-dd');
    }
    return {
      customer: queryCustomer.value,
      date,
    };
  });

  const isFetchingData = ref(false);
  const isSendingReport = ref(false);
  const isDownloadingPDF = ref(false);
  const isDownloadingCSV = ref(false);

  const etaReportData = ref<ETAReportResponseData>({
    count: 0,
    next: null,
    previous: null,
    results: [],
  });

  function search(formData: EtaReportSearchFormValidData): void {
    const query = { ...route.query, ...formData };
    const isReplace = _isEqual(route.query, query);
    void router.push({
      name: routeNames.ETA_REPORT,
      query,
      force: true,
      replace: isReplace,
    });
  }

  async function fetchData(newQuery: LocationQuery): Promise<void> {
    // TODO page/page_size for endpoint
    const curPage = queryCurPage.value;
    const pageSize = queryPageSize.value;

    const customer = queryCustomer.value;
    const date = queryDate.value;
    etaReportData.value = {
      count: 0,
      next: null,
      previous: null,
      results: [],
    };
    if (!customer) return;
    if (!date) return;
    isFetchingData.value = true;
    await fetchGetETAReport(userTimeZone.value, {
      customer,
      date,
      page: curPage,
      page_size: pageSize,
    })
      .then((data) => {
        if (!_isEqual(route.query, newQuery)) {
          return;
        }
        etaReportData.value = data;
      })
      .finally(() => {
        isFetchingData.value = false;
      });
  }

  function handleSendClick(): void {
    const customer = queryCustomer.value;
    const date = queryDate.value;
    if (!customer) return;
    if (!date) return;
    isSendingReport.value = true;
    void fetchSendETAReport(userTimeZone.value, { customer, date }).finally(
      () => {
        isSendingReport.value = false;
      },
    );
  }

  function downloadPdfETAReport(): void {
    const customer = queryCustomer.value;
    const date = queryDate.value;
    if (!customer) return;
    if (!date) return;
    isDownloadingPDF.value = true;
    void fetchGetReportEtaFile(userTimeZone.value, {
      customer,
      date,
      type: 'PDF',
    }).finally(() => {
      isDownloadingPDF.value = false;
    });
  }

  function downloadCsvETAReport(): void {
    const customer = queryCustomer.value;
    const date = queryDate.value;
    if (!customer) return;
    if (!date) return;
    isDownloadingCSV.value = true;
    void fetchGetReportEtaFile(userTimeZone.value, {
      customer,
      date,
      type: 'CSV',
    }).finally(() => {
      isDownloadingCSV.value = false;
    });
  }

  watch(
    route,
    async (newRoute) => {
      if (newRoute.name === routeNames.ETA_REPORT) {
        await fetchData(newRoute.query);
      }
    },
    { immediate: true },
  );

  return {
    search,
    initialData,
    isFetchingData,
    isSendingReport,
    isDownloadingPDF,
    isDownloadingCSV,
    etaReportData,
    handleSendClick,
    downloadPdfETAReport,
    downloadCsvETAReport,
  };
}
